import classNames from 'classnames'
import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Smartphone, SportsEsports } from '@mui/icons-material'

import { FileUploadResponse } from '../../../../api/core'
import IconPlaceholder from '../../../../assets/images/game-icon-placeholder.jpg'
import { DiscontinuedSkullIcon } from '../../../../components/DiscontinuedSkullIcon/DiscontinuedSkullIcon'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { useGetGameIconUrl } from '../../hooks/gameHooks'
import { AppType, GamePlatform } from '../../types/Game'
import './GameIcon.scss'

const BADGE_SHAKE_INTERVAL_MS = 85
const BADGE_SHAKE_TRANSITION = 'transform 0.1s ease-out'
const BADGE_RETURN_TRANSITION = 'transform 0.5s ease-out'
const BADGE_MAX_OFFSET_PX = 1
const BADGE_MAX_ROTATION_DEG = 12

const formatBadgeTransform = (translateX: number, translateY: number, angle: number) => `translate(${translateX}px, ${translateY}px) rotate(${angle}deg)`

const getBadgeTransform = (element: SVGSVGElement) => {
  const { transform } = window.getComputedStyle(element)

  if (!transform || transform === 'none') {
    return { translateX: 0, translateY: 0, angle: 0 }
  }

  const matrix = new DOMMatrix(transform)

  return {
    translateX: Math.round(matrix.e * 10) / 10,
    translateY: Math.round(matrix.f * 10) / 10,
    angle: Math.round(Math.atan2(matrix.b, matrix.a) * (180 / Math.PI)),
  }
}

const randomBadgeOffset = () => ({
  translateX: Math.round((Math.random() - 0.5) * BADGE_MAX_OFFSET_PX * 2),
  translateY: Math.round((Math.random() - 0.5) * BADGE_MAX_OFFSET_PX * 2),
  angle: Math.round((Math.random() - 0.5) * BADGE_MAX_ROTATION_DEG * 2),
})

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

interface Props {
  gameName: string
  src?: string | FileUploadResponse | string[] | FileUploadResponse[]
  selected?: boolean
  size?: 'huge' | 'large' | 'normal' | 'small' | 'tiny' | 'xtiny'
  disabled?: boolean
  rootStyle?: CSSProperties
  appType?: AppType | undefined
  gamePlatforms?: GamePlatform[] | undefined
  discontinued?: boolean
}

const GameIcon: React.FC<Props> = ({ gameName, src, selected, size, disabled, rootStyle, appType, gamePlatforms, discontinued }: Props) => {
  const iconSize = 'GameIcon--' + (size || 'normal')
  const className = classNames('GameIcon', iconSize, {
    'GameIcon--selected': selected,
    'GameIcon--disabled': disabled,
    'GameIcon--discontinued': discontinued,
  })
  const { t } = useTranslation()
  const badgeIconRef = useRef<SVGSVGElement>(null)
  const shakeIntervalRef = useRef<ReturnType<typeof setInterval>>()
  const [badgeIconStyle, setBadgeIconStyle] = useState<CSSProperties>()

  const gameIconUrl = (): string | FileUploadResponse => {
    if (Array.isArray(src)) {
      return src[src.length - 1]
    }

    return src || ''
  }

  const rawIcon = gameIconUrl()
  const hasIconSource = Boolean(typeof rawIcon === 'string' ? rawIcon.trim() : rawIcon?.url?.trim())
  const [preview] = useGetGameIconUrl(hasIconSource ? rawIcon : undefined)
  const [imgFailed, setImgFailed] = useState(false)

  useEffect(() => {
    setImgFailed(false)
  }, [preview, hasIconSource])

  // The placeholder art covers every case where there is no icon to draw: none on
  // record, the caller still working out which game this is, the blob still
  // downloading, or a bitmap that came back but won't render. The tile is never
  // empty and never changes size, so the row around it doesn't reflow when the real
  // icon arrives — it just swaps in, decoded from a blob URL that is already local.
  const imageSrc = !hasIconSource || imgFailed || !preview ? IconPlaceholder : preview

  const appTypeTooltipText = useMemo(() => {
    let tooltipText = appType === AppType.MOBILE ? t('common:mobile_game') : t('common:pc_console_game')

    if (gamePlatforms && gamePlatforms.length > 0) {
      tooltipText =
        tooltipText + ` (${t('game-platforms:game_platforms')}: ` + gamePlatforms.map((platform) => t(`game-platforms:${platform}`)).join(', ') + ')'
    }

    return tooltipText
  }, [t, appType, gamePlatforms])

  const stopBadgeShake = useCallback(() => {
    if (shakeIntervalRef.current) {
      clearInterval(shakeIntervalRef.current)
      shakeIntervalRef.current = undefined
    }
  }, [])

  const applyRandomBadgeShake = useCallback(() => {
    const { translateX, translateY, angle } = randomBadgeOffset()

    setBadgeIconStyle({
      animation: 'none',
      transform: formatBadgeTransform(translateX, translateY, angle),
      transition: BADGE_SHAKE_TRANSITION,
    })
  }, [])

  useEffect(() => stopBadgeShake, [stopBadgeShake])

  const handleBadgeMouseEnter = useCallback(() => {
    if (prefersReducedMotion()) {
      return
    }

    stopBadgeShake()
    applyRandomBadgeShake()
    shakeIntervalRef.current = setInterval(applyRandomBadgeShake, BADGE_SHAKE_INTERVAL_MS)
  }, [applyRandomBadgeShake, stopBadgeShake])

  const handleBadgeMouseLeave = useCallback(() => {
    stopBadgeShake()

    const badgeIcon = badgeIconRef.current

    if (!badgeIcon || prefersReducedMotion()) {
      setBadgeIconStyle(undefined)
      return
    }

    const { translateX, translateY, angle } = getBadgeTransform(badgeIcon)

    setBadgeIconStyle({
      animation: 'none',
      transform: formatBadgeTransform(translateX, translateY, angle),
      transition: 'none',
    })

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setBadgeIconStyle({
          animation: 'none',
          transform: formatBadgeTransform(0, 0, 0),
          transition: BADGE_RETURN_TRANSITION,
        })
      })
    })
  }, [stopBadgeShake])

  const handleBadgeTransitionEnd = useCallback((event: React.TransitionEvent<SVGSVGElement>) => {
    if (event.propertyName === 'transform') {
      setBadgeIconStyle(undefined)
    }
  }, [])

  return (
    <div className={className} style={rootStyle}>
      <img alt={gameName} title={gameName} src={imageSrc} loading="lazy" decoding="async" onError={() => setImgFailed(true)} />
      {appType && (
        <div className="GameIcon__TypeIndicator" title={appTypeTooltipText}>
          {appType === AppType.MOBILE && <Smartphone />}
          {appType === AppType.PC_CONSOLE && <SportsEsports />}
        </div>
      )}
      {discontinued && (
        <GRTooltip content={t('live-events:discontinued_tracking_tooltip')}>
          <span className="GameIcon__discontinuedBadge" onMouseEnter={handleBadgeMouseEnter} onMouseLeave={handleBadgeMouseLeave}>
            <DiscontinuedSkullIcon
              ref={badgeIconRef}
              className="GameIcon__discontinuedBadgeIcon"
              style={badgeIconStyle}
              onTransitionEnd={handleBadgeTransitionEnd}
            />
          </span>
        </GRTooltip>
      )}
    </div>
  )
}

export default GameIcon
